import {Pencil} from 'lucide-react';
import React, { useEffect, useId, useLayoutEffect, useRef, useState } from "react";

// Native select popups are positioned by the OS and cannot set their opening
// scroll position. Keep a controlled list so past events remain above the start.
export default function SessionEventPicker({ options, value, onChange, disabled, onEdit, editDisabled = disabled }) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const container = useRef(null);
  const trigger = useRef(null);
  const list = useRef(null);
  const items = useRef([]);
  const placeholder = Math.max(0, options.findIndex((option) => option.value === ""));
  const selected = options.find((option) => option.value === value) || options[placeholder];

  useLayoutEffect(() => {
    if (!open) return;
    const menu = list.current;
    const anchor = items.current[placeholder];
    if (!menu || !anchor) return;
    // Enough trailing space lets the anchor reach the top even with no future events.
    menu.style.paddingBottom = "0px";
    menu.style.paddingBottom = `${Math.max(0, menu.clientHeight - (menu.scrollHeight - anchor.offsetTop))}px`;
    menu.scrollTop = anchor.offsetTop;
    menu.focus({ preventScroll: true });
    setActive(placeholder);
  }, [open, placeholder]);

  useEffect(() => {
    if (!open) return undefined;
    const dismiss = (event) => {
      if (!container.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener("pointerdown", dismiss);
    return () => document.removeEventListener("pointerdown", dismiss);
  }, [open]);

  useEffect(() => { setOpen(false); }, [disabled]);

  function choose(index) {
    if (disabled) return;
    onChange(options[index].value);
    setOpen(false);
    trigger.current?.focus({ preventScroll: true });
  }

  function move(index) {
    const next = Math.max(0, Math.min(options.length - 1, index));
    setActive(next);
    const menu = list.current;
    const item = items.current[next];
    if (item.offsetTop < menu.scrollTop) menu.scrollTop = item.offsetTop;
    else if (item.offsetTop + item.offsetHeight > menu.scrollTop + menu.clientHeight) {
      menu.scrollTop = item.offsetTop + item.offsetHeight - menu.clientHeight;
    }
  }

  return (
    <div className="CalendarMatchLinker__picker" ref={container}
      onBlur={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false); }}>
      <span id={`${id}-label`}>Select your Event</span>
      <div className={`CalendarMatchLinker__eventControl${onEdit ? " CalendarMatchLinker__eventControl--editable" : ""}`}>
      <button type="button" ref={trigger} className="CalendarMatchLinker__eventTrigger"
        aria-labelledby={`${id}-label ${id}-value`} aria-haspopup="listbox"
        aria-expanded={open} aria-controls={open ? `${id}-list` : undefined} disabled={disabled}
        onClick={() => setOpen(!open)}
        onKeyDown={(event) => {
          if (event.key === "ArrowDown" || event.key === "ArrowUp") {
            event.preventDefault(); setOpen(true);
          }
        }}>
        <span id={`${id}-value`}>{selected?.label}</span>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="m6 9 6 6 6-6"/></svg>
      </button>
      {onEdit && <button type="button" className="CalendarMatchLinker__editEvent" aria-label="Edit shared setup" title="Edit shared setup" disabled={editDisabled} onClick={()=>{setOpen(false);onEdit();}}><Pencil size={16}/></button>}
      </div>
      {open && (
        <div ref={list} id={`${id}-list`} className="CalendarMatchLinker__eventList"
          role="listbox" tabIndex={-1} aria-labelledby={`${id}-label`}
          aria-activedescendant={`${id}-option-${active}`}
          onKeyDown={(event) => {
            if (["ArrowDown", "ArrowUp", "Home", "End", "Enter", " ", "Escape"].includes(event.key)) event.preventDefault();
            if (event.key === "ArrowDown") move(active + 1);
            if (event.key === "ArrowUp") move(active - 1);
            if (event.key === "Home") move(0);
            if (event.key === "End") move(options.length - 1);
            if (event.key === "Enter" || event.key === " ") choose(active);
            if (event.key === "Escape") { setOpen(false); trigger.current?.focus(); event.stopPropagation(); }
          }}>
          {options.map((option, index) => (
            <div key={option.value} ref={(element) => { items.current[index] = element; }}
              id={`${id}-option-${index}`} role="option" aria-selected={value === option.value}
              className={`CalendarMatchLinker__eventOption${active === index ? " is-focused" : ""}`}
              onMouseDown={(event) => event.preventDefault()} onClick={() => choose(index)}>
              <strong>{option.label}</strong>
              <span>{option.group}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
